"use client";

import React from "react";
import { Check, Mail, Phone, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const benefits = ["Elite player development pathways", "State-of-the-art training facilities", "Strong community and family values", "Professional coaching staff", "Rich heritage since 1933"];
const productOptions = ["General Inquiry", "Junior Academy", "Senior Football", "Sponsorship"];
function QuoteFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <Check className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-bold font-default text-foreground">Message Sent</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Your Name <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Enter your full name" className="bg-background border-border focus:border-primary rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Organization</FormLabel>
              <FormControl>
                <Input placeholder="Optional" className="bg-background border-border focus:border-primary rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Email Address <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" className="bg-background border-border focus:border-primary rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 000 000 000" className="bg-background border-border focus:border-primary rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="product" rules={{
        required: "Please select a department"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Department <span className="text-primary">*</span></FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background border-border focus:border-primary rounded-sm">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border">
                  {productOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i} className="focus:bg-primary focus:text-primary-foreground">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="quantity" rules={{
        min: {
          value: 1,
          message: "Quantity must be at least 1"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Number of Players</FormLabel>
              <FormControl>
                <Input type="number" min="1" placeholder="e.g., 1" className="bg-background border-border focus:border-primary rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" rules={{
      required: "Message details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">Your Message <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you today?" className="min-h-[120px] resize-y bg-background border-border focus:border-primary rounded-sm" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">Include any specific questions or team interests.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" size="lg" className="w-full text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm transition-all duration-300" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function RequestATechnicalQuote() {
  return <section data-section-id="223513" id="reach-out-to-the-club" className="w-full bg-background py-16 md:py-24 px-6 md:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        
        <div className="flex flex-col space-y-8">
          <div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/wt_birkalla_logo_gold.png" alt="WT Birkalla Logo" className="h-20 w-auto object-contain mb-6" loading="lazy" />
            <h2 className="text-4xl md:text-5xl font-bold font-default text-foreground tracking-tighter mb-4 uppercase">
              Join the Birkalla Family
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed font-default">
              Whether you're looking to join our junior academy, trial for the senior squad, or partner with us through our business network, we want to hear from you.
            </p>
          </div>

          <ul className="space-y-4">
            {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                <div className="mt-1 shrink-0 bg-primary/10 p-1 rounded-full">
                  <Check className="size-5 text-primary" />
                </div>
                <span className="text-base text-foreground font-medium pt-1 font-default">
                  {benefit}
                </span>
              </li>)}
          </ul>

          <div className="pt-8 border-t border-border mt-8">
            <h3 className="text-xl font-bold font-default text-foreground mb-4 uppercase tracking-tight">
              Club Contacts
            </h3>
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex items-center gap-3">
                <div className="bg-secondary/10 p-2 rounded-sm">
                  <Phone className="size-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-mono">Call Us</p>
                  <Link to="tel:+61882345678" className="text-foreground font-medium hover:text-primary transition-colors font-default">
                    (08) 8234 5678
                  </Link>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-secondary/10 p-2 rounded-sm">
                  <Mail className="size-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-mono">Email Us</p>
                  <Link to="mailto:admin@wtbirkalla.com.au" className="text-foreground font-medium hover:text-primary transition-colors font-default">
                    admin@wtbirkalla.com.au
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 bg-primary/10 rounded-3xl -z-10 blur-3xl hidden lg:block" />
          
          <Card className="border-t-2 border-t-primary border-x-border border-b-border shadow-2xl bg-card/50 backdrop-blur-md overflow-hidden rounded-sm">
            <CardHeader className="bg-muted/10 border-b border-border pb-6">
              <CardTitle className="text-2xl font-bold font-default text-foreground uppercase tracking-tight">
                Get in Touch
              </CardTitle>
              <CardDescription className="text-base font-default text-muted-foreground">
                Complete the form below and a club representative will get back to you shortly.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <FormProvider formKey="contact_form_get_in_touch" formId="contact_form_get_in_touch" sectionName="ReachOutToTheClub" defaultValues={{
              name: "",
              company: "",
              email: "",
              phone: "",
              product: "",
              quantity: "",
              details: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              product: {
                required: true
              },
              details: {
                required: true
              },
              quantity: {
                min: 1
              }
            }} submitText="Send Message" successMessage="Thank you for reaching out to WT Birkalla. Our team will review your message and respond as soon as possible.">
                <QuoteFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>

      </div>
    </section>;
}